/**
 * Gerenciador de downloads (PLAN §18).
 *
 * Premissa que molda o desenho: **no iOS a PWA é suspensa sem aviso**. Não dá para
 * assumir que um download em lote continua rodando em segundo plano. Portanto:
 *
 *  - a fila vive no IndexedDB, não em memória;
 *  - o progresso parcial nunca é dado como concluído;
 *  - ao reabrir, o que ficou pela metade volta para a fila (F20-T06);
 *  - o trabalho é retomado quando o app volta ao primeiro plano.
 */
import {
  claimNext,
  countByStatus,
  enqueue,
  enqueueMany,
  getJob,
  listJobs,
  removeJob,
  updateJob,
} from '@/db/repositories/downloads';
import { updateState } from '@/db/repositories/localSignState';
import { db, type DownloadJob } from '@/db/schema';
import type { MediaResolver } from '@/services/media/MediaResolver';
import { validateMediaResponse, validateVideoBlob } from '@/services/media/validation';
import { getFileStore } from '@/storage/index';
import { StorageQuotaError, type OfflineFileStore } from '@/storage/types';
import type { Sign } from '@/types/catalog';

/** Concorrência inicial (F12-T04). Dois é o equilíbrio entre velocidade e memória. */
export const DEFAULT_CONCURRENCY = 2;

const MAX_ATTEMPTS = 3;

/** Classificação do erro, porque a ação do usuário difere em cada caso (F12-T16). */
export type DownloadErrorKind = 'network' | 'quota' | 'server' | 'invalid-media' | 'cancelled';

export interface DownloadProgress {
  signId: string;
  bytesDownloaded: number;
  totalBytes?: number;
  /** 0..1 quando o tamanho total é conhecido. */
  ratio?: number;
}

export interface DownloadManagerEvents {
  onProgress?: (progress: DownloadProgress) => void;
  onCompleted?: (signId: string) => void;
  onFailed?: (signId: string, kind: DownloadErrorKind, message: string) => void;
  onQueueChanged?: () => void;
}

export class DownloadManager {
  /** Drenagem em andamento. Awaitável, para que quem enfileira possa esperar o fim. */
  private draining: Promise<void> | null = null;
  private paused = false;
  private disposed = false;
  private readonly active = new Map<string, AbortController>();
  /** Timers de backoff pendentes, para poderem ser cancelados no descarte. */
  private readonly retryTimers = new Set<ReturnType<typeof setTimeout>>();

  constructor(
    private readonly resolver: MediaResolver,
    private readonly events: DownloadManagerEvents = {},
    private readonly store: OfflineFileStore = getFileStore(),
    private readonly concurrency: number = DEFAULT_CONCURRENCY,
  ) {}

  // --- enfileiramento ---

  /** Enfileira um sinal (F12-T17). */
  async queueSign(sign: Sign): Promise<boolean> {
    if (!this.resolver.canDownload(sign)) return false;
    await enqueue(sign.id);
    this.events.onQueueChanged?.();
    void this.start().catch(() => undefined);
    return true;
  }

  /** Enfileira todos os sinais baixáveis de uma categoria (F12-T18). */
  async queueCategory(categoryId: string): Promise<number> {
    const signs = await db.signs.where('categoryId').equals(categoryId).toArray();
    return this.queueMany(signs, categoryId);
  }

  /** Enfileira o glossário inteiro (F12-T19). */
  async queueAll(): Promise<number> {
    const signs = await db.signs.filter((sign) => sign.active).toArray();
    return this.queueMany(signs);
  }

  private async queueMany(signs: Sign[], categoryId?: string): Promise<number> {
    const pending: string[] = [];

    for (const sign of signs) {
      if (!this.resolver.canDownload(sign)) continue;
      // Já disponível offline e atualizado: não rebaixar (F12-T20).
      if (await this.store.exists(sign.id)) {
        const state = await db.localSignState.get(sign.id);
        if (!state?.offline.outdated) continue;
      }
      pending.push(sign.id);
    }

    const added = await enqueueMany(pending, categoryId ? { originCategoryId: categoryId } : {});
    this.events.onQueueChanged?.();
    void this.start().catch(() => undefined);
    return added;
  }

  /**
   * Recalcula o que ainda falta depois de uma atualização do catálogo (F12-T21).
   *
   * Enfileira apenas o que ficou desatualizado — o vídeo mudou na fonte — e sinais
   * novos que já tinham sido pedidos antes.
   */
  async requeueOutdated(): Promise<number> {
    const states = await db.localSignState
      .filter((state) => Boolean(state.offline.outdated))
      .toArray();
    if (states.length === 0) return 0;

    const signs = (await db.signs.bulkGet(states.map((state) => state.signId))).filter(
      (sign): sign is Sign => sign !== undefined,
    );

    return this.queueMany(signs);
  }

  // --- controle ---

  pause(): void {
    this.paused = true;
    for (const controller of this.active.values()) controller.abort();
    this.active.clear();
    this.events.onQueueChanged?.();
  }

  resume(): void {
    this.paused = false;
    void this.start().catch(() => undefined);
  }

  isPaused(): boolean {
    return this.paused;
  }

  async cancel(signId: string): Promise<void> {
    this.active.get(signId)?.abort();
    this.active.delete(signId);
    await removeJob(signId);
    // O arquivo temporário some junto: parcial não vira nada aproveitável.
    await this.store.remove(signId).catch(() => undefined);
    await updateState(signId, (current) => ({ ...current, offline: { status: 'none' } }));
    this.events.onQueueChanged?.();
  }

  async cancelAll(): Promise<void> {
    for (const job of await listJobs()) {
      if (job.status === 'done') continue;
      await this.cancel(job.signId);
    }
  }

  // --- execução ---

  /**
   * Encerra o gerenciador: aborta o que está em andamento e cancela os timers de
   * backoff pendentes.
   *
   * Sem isto, um timer agendado continua disparando depois que o app trocou de
   * contexto e escreve num banco que já não é o dele.
   */
  dispose(): void {
    this.disposed = true;
    for (const timer of this.retryTimers) clearTimeout(timer);
    this.retryTimers.clear();
    for (const controller of this.active.values()) controller.abort();
    this.active.clear();
  }

  /**
   * Processa a fila respeitando a concorrência.
   *
   * Chamar de novo enquanto já está processando **devolve a mesma promessa** em vez
   * de virar no-op: assim quem enfileira consegue aguardar o término de verdade.
   */
  async start(): Promise<void> {
    if (this.paused || this.disposed) return;
    if (this.draining) return this.draining;

    this.draining = this.drain().finally(() => {
      this.draining = null;
    });

    return this.draining;
  }

  private async drain(): Promise<void> {
    for (;;) {
      // `disposed` precisa ser checado a cada volta, e não só na entrada: sem isso
      // uma drenagem descartada continua puxando o próximo lote da fila.
      if (this.paused || this.disposed) return;

      const batch = await claimNext(this.concurrency);
      if (batch.length === 0) return;

      await Promise.all(batch.map((job) => this.run(job)));
    }
  }

  private async run(job: DownloadJob): Promise<void> {
    const { signId } = job;
    if (this.active.has(signId)) return;

    const sign = await db.signs.get(signId);
    if (!sign) {
      await removeJob(signId);
      return;
    }

    const controller = new AbortController();
    this.active.set(signId, controller);

    await updateJob(signId, {
      status: 'downloading',
      startedAt: new Date().toISOString(),
      attempts: job.attempts + 1,
    });
    await updateState(signId, (current) => ({
      ...current,
      offline: { ...current.offline, status: 'downloading', bytesDownloaded: 0 },
    }));
    this.events.onQueueChanged?.();

    try {
      const resolution = await this.resolver.resolveDownload(sign);
      if (!resolution.url) {
        throw new DownloadFailure('invalid-media', 'Nenhuma origem de mídia para este sinal.');
      }

      const response = await fetch(resolution.url, { signal: controller.signal });

      const responseCheck = validateMediaResponse(response);
      if (!responseCheck.valid) {
        const kind: DownloadErrorKind = response.status >= 500 ? 'server' : 'invalid-media';
        throw new DownloadFailure(kind, responseCheck.issues[0]?.message ?? 'Resposta inválida.');
      }

      await this.store.write(signId, response, {
        ...(resolution.expectedBytes !== undefined
          ? { expectedBytes: resolution.expectedBytes }
          : {}),
        signal: controller.signal,
        onProgress: ({ bytesWritten, totalBytes }) => {
          // Escritas de progresso são best-effort: perder uma não invalida o
          // download, e deixá-las rejeitar sem tratamento derruba o app quando o
          // banco fecha no meio (troca de contexto, reset, aba encerrada).
          void updateJob(signId, {
            bytesDownloaded: bytesWritten,
            ...(totalBytes ? { totalBytes } : {}),
          }).catch(() => undefined);

          void updateState(signId, (current) => ({
            ...current,
            offline: {
              ...current.offline,
              status: 'downloading',
              bytesDownloaded: bytesWritten,
              ...(totalBytes ? { totalBytes } : {}),
            },
          })).catch(() => undefined);
          this.events.onProgress?.({
            signId,
            bytesDownloaded: bytesWritten,
            ...(totalBytes ? { totalBytes, ratio: bytesWritten / totalBytes } : {}),
          });
        },
      });

      // O arquivo está gravado; agora prova-se que ele é um vídeo de verdade
      // (PLAN §39). Falhar aqui é melhor que descobrir no avião.
      const blob = await this.store.read(signId);
      const validation = await validateVideoBlob(blob, {
        ...(resolution.expectedBytes !== undefined
          ? { expectedBytes: resolution.expectedBytes }
          : {}),
        ...(resolution.checksum ? { expectedChecksum: resolution.checksum } : {}),
      });

      if (!validation.valid) {
        await this.store.remove(signId);
        throw new DownloadFailure(
          'invalid-media',
          validation.issues.map((issue) => issue.message).join(' '),
        );
      }

      await updateJob(signId, {
        status: 'done',
        finishedAt: new Date().toISOString(),
        bytesDownloaded: blob.size,
        totalBytes: blob.size,
      });
      await updateState(signId, (current) => ({
        ...current,
        offline: {
          status: 'available',
          bytesDownloaded: blob.size,
          totalBytes: blob.size,
          localFileName: `${signId}.mp4`,
          ...(resolution.checksum ? { checksum: resolution.checksum } : {}),
          ...(sign.media.externalId ? { sourceExternalId: sign.media.externalId } : {}),
          sourceProvider: sign.media.provider,
          downloadedAt: new Date().toISOString(),
          outdated: false,
        },
      }));

      this.events.onCompleted?.(signId);
    } catch (error) {
      await this.handleFailure(signId, job.attempts + 1, error);
    } finally {
      this.active.delete(signId);
      this.events.onQueueChanged?.();
    }
  }

  private async handleFailure(signId: string, attempts: number, error: unknown): Promise<void> {
    const { kind, message } = classifyError(error);

    // Cancelamento não é falha e não deve consumir tentativa nem virar erro.
    if (kind === 'cancelled') return;

    // Cota estourada não adianta repetir: o espaço não vai aparecer sozinho.
    const retryable = kind === 'network' || kind === 'server';
    const canRetry = retryable && attempts < MAX_ATTEMPTS;

    if (canRetry) {
      // Backoff simples: 2s, 4s. Suficiente para uma oscilação de rede passar.
      const delayMs = 2000 * 2 ** (attempts - 1);
      await updateJob(signId, {
        status: 'queued',
        lastError: message,
        retryAfter: new Date(Date.now() + delayMs).toISOString(),
      });
      await updateState(signId, (current) => ({
        ...current,
        offline: { ...current.offline, status: 'queued' },
      }));

      const timer = setTimeout(() => {
        this.retryTimers.delete(timer);
        if (!this.disposed) void this.start().catch(() => undefined);
      }, delayMs);
      this.retryTimers.add(timer);
      return;
    }

    await updateJob(signId, { status: 'error', lastError: message });
    await updateState(signId, (current) => ({
      ...current,
      offline: { ...current.offline, status: 'error', error: message },
    }));
    this.events.onFailed?.(signId, kind, message);
  }

  async stats(): Promise<Record<string, number>> {
    return countByStatus();
  }

  async jobFor(signId: string): Promise<DownloadJob | undefined> {
    return getJob(signId);
  }
}

export class DownloadFailure extends Error {
  constructor(
    readonly kind: DownloadErrorKind,
    message: string,
  ) {
    super(message);
    this.name = 'DownloadFailure';
  }
}

/** Traduz qualquer erro para uma causa acionável pelo usuário (F12-T16). */
export function classifyError(error: unknown): { kind: DownloadErrorKind; message: string } {
  if (error instanceof DownloadFailure) return { kind: error.kind, message: error.message };

  if (error instanceof StorageQuotaError) {
    return { kind: 'quota', message: error.message };
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return { kind: 'cancelled', message: 'Download cancelado.' };
  }

  if (error instanceof TypeError) {
    // `fetch` rejeita com TypeError quando a rede simplesmente não está lá.
    return { kind: 'network', message: 'Sem conexão para baixar este vídeo.' };
  }

  return {
    kind: 'network',
    message: error instanceof Error ? error.message : 'Falha desconhecida no download.',
  };
}
